<template>
    <div class="asset-versions">
        <div class="d-flex flex-wrap align-items-center mb-3">
            <b-form-select
                v-model="assetTypeFilter"
                :options="assetTypeFilterOptions"
                style="max-width: 220px"
                class="mr-2"
                @change="fetchAssets"
            />
            <span class="text-muted mr-auto">총 {{ assets.length }}개 버전</span>
            <b-button variant="outline-secondary" @click="$emit('check-gate')">
                골든 게이트 확인
            </b-button>
        </div>

        <b-table
            :items="assets"
            :fields="fields"
            :busy="loading"
            hover
            responsive
            show-empty
            empty-text="저장된 버전이 없습니다. 피드백 인박스에서 첫 버전을 만들어 주세요."
        >
            <template #cell(asset_type)="row">
                {{ assetTypeLabel(row.item.asset_type) }}
                <div class="small text-muted">{{ row.item.asset_type }}</div>
            </template>

            <template #cell(version)="row">
                v{{ row.item.version }}
                <div v-if="row.item.base_version" class="small text-muted">
                    기준 v{{ row.item.base_version }}
                </div>
            </template>

            <template #cell(is_active)="row">
                <b-badge v-if="row.item.is_active" variant="success">활성</b-badge>
                <span v-else class="text-muted">-</span>
            </template>

            <template #cell(note)="row">
                <div class="asset-note" :title="row.item.note || ''">{{ row.item.note || '-' }}</div>
            </template>

            <template #cell(created_at)="row">
                {{ formatDateTime(row.item.created_at) }}
            </template>

            <template #cell(actions)="row">
                <b-button
                    size="sm"
                    variant="outline-secondary"
                    class="mr-1"
                    @click="openContent(row.item)"
                >
                    내용 보기
                </b-button>
                <b-button
                    v-if="!row.item.is_active"
                    size="sm"
                    variant="outline-primary"
                    :disabled="activatingId === row.item.id"
                    @click="activate(row.item)"
                >
                    이 버전 활성화
                </b-button>
            </template>
        </b-table>

        <!-- 콘텐츠 전문 -->
        <b-modal
            id="asset-content-modal"
            size="xl"
            scrollable
            ok-only
            ok-title="닫기"
            :title="contentTitle"
        >
            <div v-if="contentLoading" class="text-center text-muted py-4">
                불러오는 중입니다.
            </div>
            <pre v-else class="asset-content">{{ contentAsset && contentAsset.content }}</pre>
        </b-modal>
    </div>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import {
    ASSET_TYPE_OPTIONS,
    assetTypeLabel,
    formatDateTime,
} from './prompt-labels';

export default {
    name: 'AssetVersionsTab',
    data() {
        return {
            assets: [],
            loading: false,
            assetTypeFilter: '',
            activatingId: null,
            contentAsset: null,
            contentLoading: false,
            assetTypeFilterOptions: [
                {value: '', text: '전체 자산'},
                ...ASSET_TYPE_OPTIONS,
            ],
            fields: [
                {key: 'asset_type', label: '자산', thStyle: {width: '160px'}},
                {key: 'version', label: '버전', thStyle: {width: '100px'}},
                {key: 'is_active', label: '상태', thStyle: {width: '80px'}},
                {key: 'note', label: '무엇이 달라졌나'},
                {key: 'created_at', label: '저장', thStyle: {width: '130px'}},
                {key: 'actions', label: '관리', thStyle: {width: '210px'}},
            ],
        };
    },
    computed: {
        contentTitle() {
            if (!this.contentAsset) return '버전 내용';
            return `${assetTypeLabel(this.contentAsset.asset_type)} v${this.contentAsset.version}`;
        },
    },
    created() {
        this.fetchAssets();
    },
    methods: {
        assetTypeLabel,
        formatDateTime,
        async fetchAssets() {
            this.loading = true;
            try {
                const params = this.assetTypeFilter ? {asset_type: this.assetTypeFilter} : {};
                const {data} = await this.$axios.get('/supplement-prompt/assets', {params});
                this.assets = (data && data.assets) || [];
            } catch (e) {
                this.$bvToast.toast(getErrorMessage(e, '버전 목록을 불러오지 못했습니다.'), {
                    title: '오류', variant: 'danger',
                });
            } finally {
                this.loading = false;
            }
        },
        async openContent(asset) {
            // 목록 응답에는 content 가 빠져 있어 단건 조회로 전문을 받아온다.
            this.contentAsset = {asset_type: asset.asset_type, version: asset.version, content: ''};
            this.contentLoading = true;
            this.$bvModal.show('asset-content-modal');
            try {
                const {data} = await this.$axios.get(`/supplement-prompt/assets/${asset.id}`);
                this.contentAsset = data;
            } catch (e) {
                this.$bvModal.hide('asset-content-modal');
                this.$bvToast.toast(getErrorMessage(e, '버전 내용을 불러오지 못했습니다.'), {
                    title: '오류', variant: 'danger',
                });
            } finally {
                this.contentLoading = false;
            }
        },
        async activate(asset) {
            const ok = await this.$bvModal.msgBoxConfirm(
                `${assetTypeLabel(asset.asset_type)} v${asset.version} 을 활성본으로 전환합니다. `
                + '활성화 후 60초 내 판정 파이프라인에 반영됩니다. '
                + '활성화 전 수렴 지표 탭에서 골든 게이트를 확인하는 것을 권장합니다.',
                {title: '활성화 확인', okTitle: '활성화', cancelTitle: '취소'},
            );
            if (!ok) return;

            this.activatingId = asset.id;
            try {
                await this.$axios.post(`/supplement-prompt/assets/${asset.id}/activate`);
                this.$bvToast.toast(
                    `${assetTypeLabel(asset.asset_type)} v${asset.version} 을 활성화했습니다.`,
                    {title: '완료', variant: 'success'},
                );
                await this.fetchAssets();
            } catch (e) {
                // 저장 당시 통과했어도 활성화 시점에 검증 규칙이 강화됐을 수 있다 (서버 400).
                this.$bvToast.toast(getErrorMessage(e, '활성화에 실패했습니다.'), {
                    title: '오류', variant: 'danger',
                });
            } finally {
                this.activatingId = null;
            }
        },
    },
};
</script>

<style scoped>
/* 메모에는 생성 당시의 "무엇이 달라지나요" 요약이 통째로 들어간다(2~4문장).
   목록에서는 3줄까지만 보여 행 높이가 튀지 않게 하고, 전문은 tooltip 으로 준다. */
.asset-note {
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 0.9rem;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.asset-content {
    white-space: pre-wrap;
    word-break: break-word;
    font-family: Consolas, Monaco, 'Courier New', monospace;
    font-size: 0.85rem;
    margin-bottom: 0;
}
</style>
